import { Button, Card, Text } from '@ui/themed';
import type { PropsWithChildren, ReactNode } from 'react';
import type { IconName } from './ui/icon/types';

export function EmptyStateHero({
  eyebrow,
  title,
  description,
  children,
}: PropsWithChildren<{ eyebrow: string; title: string; description: string }>) {
  return (
    <div className="relative bg-platform-primary/6 px-6 py-8 sm:px-10 sm:py-10">
      <div
        aria-hidden
        className="mask-[radial-gradient(ellipse_at_top_left,black,transparent_80%)] absolute inset-0 bg-[linear-gradient(to_right,color-mix(in_srgb,currentColor_8%,transparent)_1px,transparent_1px),linear-gradient(to_bottom,color-mix(in_srgb,currentColor_8%,transparent)_1px,transparent_1px)] bg-size-[28px_28px] text-platform-primary"
      />

      <div className="relative flex flex-col justify-between gap-8 lg:flex-row lg:items-center">
        <div className="flex flex-col items-start gap-4">
          <span className="flex items-center gap-2 rounded-full border border-platform-primary/25 bg-white px-3.5 py-1.5 font-mono font-semibold text-platform-primary text-xs">
            <span aria-hidden className="relative flex size-1.5">
              <span className="absolute inset-0 rounded-full bg-platform-primary opacity-75 motion-safe:animate-ping" />
              <span className="relative size-1.5 rounded-full bg-platform-primary" />
            </span>
            {eyebrow}
          </span>

          <h2 className="font-semibold text-3xl text-spark-text-primary sm:text-4xl">{title}</h2>

          <p className="max-w-xl text-spark-text-secondary">{description}</p>
        </div>

        {children}
      </div>
    </div>
  );
}

export function HeroPreviewCard({ children }: PropsWithChildren) {
  return (
    <div
      aria-hidden
      className="hidden w-80 shrink-0 self-center rounded-xl bg-white p-4 shadow-xl md:block lg:self-auto"
    >
      {children}
    </div>
  );
}

export function EmptyStateCtaCard({
  badge,
  title,
  description,
  cta,
  ctaIcon,
  onClick,
  highlighted,
}: {
  badge?: string;
  title: string;
  description: ReactNode;
  cta: string;
  ctaIcon?: IconName;
  onClick: () => void;
  highlighted?: boolean;
}) {
  return (
    <Card fullHeight>
      <Card.Header
        headingLevel="h3"
        badges={badge ? [{ text: badge, variant: highlighted ? 'primary' : 'default' }] : undefined}
      >
        {title}
      </Card.Header>
      <Card.Body justifyBetween>
        <Text variant="secondary">{description}</Text>
        <span className="pt-2">
          <Button icon={ctaIcon} variant={highlighted ? 'primary' : 'secondary'} onClick={onClick}>
            {cta}
          </Button>
        </span>
      </Card.Body>
    </Card>
  );
}
